<template>
  <div>
    <div v-if="loading" class="dot-opacity-loader">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div v-if="downtimeData && downtimeData.length" class="pr-1">
      <apexchart
        class="mr-2"
        type="rangeBar"
        width="100%"
        :height="height || 100"
        :options="chartOptions"
        :series="dataReady"
      ></apexchart>
    </div>
    <div v-else class="row justify-content-center">
      <h4 class="p-3">{{ $t('dashboards.noData') }}</h4>
    </div>
  </div>
</template>

<script>
import workCenterEventsByShift from '@/api/boxinfo/workCenterEventsByShift.gql';
import moment from 'moment';
import ru from 'apexcharts/dist/locales/ru.json';

export default {
  name: 'BoxEventsByshiftsChart',
  components: {
    apexchart: () => import('vue-apexcharts'),
  },
  props: {
    workCenterId: String,
    period: Object,
  },
  apollo: {
    downtimeData: {
      query: workCenterEventsByShift,
      variables() {
        return {
          workCenterId: this.workCenterId,
          period: this.period,
        };
      },
      pollInterval: 30000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (queryData) {
          return queryData[Object.keys(queryData)[0]];
        } else {
          return [];
        }
      },
      skip() {
        return !this.workCenterId;
      },
      error(e) {
        this.$store.commit('SET_ERROR', e.message);
      },

      fetchPolicy: 'network-only',
    },
  },
  data() {
    return {
      downtimeData: [],
    };
  },
  computed: {
    isiOS() {
      return !!navigator.platform && /Mac|iPad|iPhone|iPod/.test(navigator.platform);
    },
    chartOptions() {
      const vm = this;
      return {
        chart: {
          id: 'workCenterEventsByShift',
          type: 'rangeBar',
          animations: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          locales: [ru],
          defaultLocale: 'ru',
        },
        grid: {
          show: false,
        },
        plotOptions: {
          bar: {
            horizontal: true,
            columnWidth: '80%',
            barHeight: '80%',
          },
        },
        legend: {
          position: 'top',
          labels: {
            colors: ['white'],
          },
        },
        yaxis: {
          show: true,
          labels: {
            show: true,
            minWidth: 30,
            style: {
              colors: 'white',
            },
            formatter: function (val) {
              return moment(val).format('DD.MM.YYYY');
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            show: false,
            style: {
              colors: 'grey',
            },
          },
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        tooltip: {
          theme: 'dark',
          custom: function ({ seriesIndex, dataPointIndex, w }) {
            const series =
              vm.dataReady && vm.dataReady.length > seriesIndex ? vm.dataReady[seriesIndex] : 0;
            const shiftColor = w.globals.colors[series.colorKey] || '#ffffff';
            const point =
              series && series.data.length > dataPointIndex ? series.data[dataPointIndex] : 0;
            const shiftName = series && series.name ? series.name : '';
            const name = point ? point.name : '';
            const period = point ? point.period : '';
            const fillColorName = point ? point.fillColor : '';
            return (
              '<div class="arrow_box p-3">' +
              `<span style="color:${shiftColor}">` +
              `${shiftName}<p style="color:${fillColorName}">${name}<p style="color:white">${period}` +
              '</span>' +
              '</div>'
            );
          },
        },
        stroke: {
          show: false,
          width: 0.5,
        },
      };
    },
    minDate() {
      return this.period.dateStart
        ? new Date(this.period.dateStart).getTime()
        : new Date().getTime();
    },
    maxDate() {
      return this.period.dateEnd ? new Date(this.period.dateEnd).getTime() : new Date().getTime();
    },
    dataReady() {
      return this.prepData(this.downtimeData);
    },
    loading() {
      return this.$apollo.queries.downtimeData.loading;
    },
    height() {
      let rowCount = new Set(this.downtimeData.map((i) => i.shiftDate + i.shiftId)).size;
      return (rowCount + 1) * 45;
    },
  },
  methods: {
    dateToTimestamp(date, isiOS) {
      return isiOS ? moment(date).add(-3, 'h').valueOf() : moment(date).valueOf();
    },
    formatDate(date, isiOS) {
      return isiOS
        ? moment(date).add(3, 'h').format('DD.MM.YYYY HH:mm:ss')
        : moment(date).format('DD.MM.YYYY HH:mm:ss');
    },
    formatPeriod(period, isiOS) {
      return `${this.formatDate(period.eventStart, isiOS)} - ${this.formatDate(
        period.eventEnd,
        isiOS,
      )}`;
    },
    prepData(values) {
      const isIOS = this.isiOS;
      const mappedVal = this.groupBy(values, (i) => i.shiftId);
      const result = [];
      let colorKey = 0;
      mappedVal.forEach((v, k) => {
        result.push({
          name: `${this.$t('box.shift')} ${k}`,
          colorKey: colorKey++,
          data: v.map((i) => {
            return {
              name: i.state.name,
              period: this.formatPeriod(i, isIOS),
              fillColor: i.state.color ? i.state.color.trim() : '#6D6E71',
              x: i.shiftId ? `${i.shiftDate}` : 'code',
              y: [i.inShiftStart, i.inShiftEnd],
            };
          }),
        });
      });
      return result;
    },
    groupBy(list, keyGetter) {
      const map = new Map();
      list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
          map.set(key, [item]);
        } else {
          collection.push(item);
        }
      });
      return map;
    },
  },
};
</script>
